<template>
                <div v-if="currentView === 'domains'">
                    <div class="page-header">
                        <div><div class="page-title">網域管理</div><div class="page-subtitle">建立、編輯及刪除郵件伺服器網域</div></div>
                    </div>
                    <div v-if="isSuperAdmin" class="card p-6 mb-6">
                        <h3 class="card-title mb-3">新增網域</h3>
                        <div class="flex gap-2 items-end">
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">網域名稱</label>
                                <input v-model="newDomain.name" type="text" placeholder="example.com"
                                    class="input">
                            </div>
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">最大用戶數 (0=不限)</label>
                                <input v-model="newDomain.max_users" type="number" class="input w-32">
                            </div>
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">最大別名數 (0=不限)</label>
                                <input v-model="newDomain.max_aliases" type="number" class="input w-32">
                            </div>
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">最大容量 (MB, 0=不限)</label>
                                <input v-model="newDomain.max_quota" type="number" class="input w-32">
                            </div>
                            <div>
                                <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">稽核信箱 (選填)</label>
                                <input v-model="newDomain.audit_email" type="email" placeholder="audit@example.com"
                                    class="input w-48">
                            </div>
                            <button @click="addDomain"
                                class="btn btn-primary mb-0.5">新增</button>
                        </div>
                    </div>
                    <div v-else class="card p-6 mb-6">
                        <div class="text-sm" style="color:var(--gray-600);">
                            網域管理員可查看並編輯本網域資訊；新增 / 刪除需要超級管理員權限。
                        </div>
                    </div>
                    <div class="card p-6">
                        <table class="data-table">
                            <thead>
                                <tr class="border-b">
                                    <th class="p-2">ID</th>
                                    <th class="p-2">名稱</th>
                                    <th class="p-2">用戶數</th>
                                    <th class="p-2">別名數</th>
                                    <th class="p-2">容量限制</th>
                                    <th class="p-2">稽核信箱</th>
                                    <th v-if="isSuperAdmin || isDomainAdmin" class="p-2">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(d, idx) in safeArr(domains)" :key="keyById(d, 'domain', idx)" class="border-b">
                                    <td class="p-2">{{ d.id }}</td>
                                    <td class="p-2 font-bold">{{ d.name }}</td>
                                    <td class="p-2">{{ d.max_users === 0 ? '無限制' : d.max_users }}</td>
                                    <td class="p-2">{{ d.max_aliases === 0 ? '無限制' : d.max_aliases }}</td>
                                    <td class="p-2">{{ d.max_quota === 0 ? '無限制' : Math.round(d.max_quota / 1024 / 1024)
                                        + ' MB' }}</td>
                                    <td class="p-2 text-sm" style="color:var(--gray-600);">{{ d.audit_email || '-' }}</td>
                                    <td v-if="isSuperAdmin || isDomainAdmin" class="p-2">
                                        <button @click="startEditDomain(d)"
                                            class="btn btn-ghost btn-xs">編輯</button>
                                        <button v-if="isSuperAdmin" @click="deleteDomain(d.id)" class="btn btn-ghost btn-xs" style="color:var(--color-danger)">刪除</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>


</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  isSuperAdmin, isDomainAdmin,
  newDomain, editingDomain, editDomainForm,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  addDomain, startEditDomain, updateDomain,
} = store
</script>

